import React, { type ReactNode } from "react";

import clsx from "clsx";
import Image from "next/image";

import CalendarIcon from "public/images/icons/calendar-icon.svg";
import CallPhoneIcon from "public/images/icons/call-phone-icon.svg";
import SupportIcon from "public/images/icons/support-icon.svg";

import Button from "components/button";
import DiscoverEnerfloButtonWithOverlay from "components/discover-enerflo-button-with-overlay";
import Heading from "components/heading";
import HeroSection, { HeroSectionContainer } from "components/hero-section";
import { sendAnalyticEvent } from "components/layout/page/use-page";
import RequestCallbackButton from "components/request-callback/button-with-overlay";

import styles from "./index.module.css";

export enum DemoSchemes {
  DARK = "dark",
  LIGHT = "light",
  NAVY = "navy",
}

type SectionProps = { title: ReactNode; action: ReactNode; icon: string; children: ReactNode };

const Section = ({ title, action, icon, children }: SectionProps) => {
  return (
    <div className="flex flex-col items-center w-full tracking-wide space-y-4 text-center sm:max-w-md lg:text-left lg:flex-1">
      <div className="grow space-y-4 lg:space-y-6">
        <Heading
          tag="h5"
          className="text-primary w-full flex flex-col gap-3 justify-center items-center lg:flex-row lg:justify-start"
        >
          <figure className="h-8 w-8 lg:h-10 lg:w-10 relative">
            <Image src={icon} alt={`${title} - Icon`} layout="fill" />
          </figure>
          {title}
        </Heading>
        <p className={styles.description}>{children}</p>
      </div>
      {action}
    </div>
  );
};

const title = "Contact Enerflo";

type Props = {
  scheme?: DemoSchemes;
};

const DemoFormSection = ({ scheme = DemoSchemes.DARK }: Props) => {
  return (
    <div className={clsx("py-4", styles[`scheme-${scheme}`])}>
      <div className="w-full mx-auto px-6 xl:px-14">
        <HeroSectionContainer title={title} className={clsx(styles.sectionContainer, "flex-col")}>
          <HeroSection.Inner className="flex-col md:space-x-0">
            <div className="flex-grow px-6 w-full sm:px-10 text-center mb-6 md:mb-14 md:text-center lg:px-14">
              <HeroSection.Header className={clsx(styles.header, "md:items-center")}>
                <Heading tag="h2">{title}</Heading>
              </HeroSection.Header>
              <HeroSection.Description className={styles.description}>
                Enerflo is available to <strong className="whitespace-nowrap">🇺🇸 US-based</strong> Residential Solar
                Companies in all 50 states, including Solar Sales Orgs, Solar Installers and EPCs, Vertically Integrated
                and Hybrid solar companies. Enerflo is not available for use outside the U.S. at this time.
              </HeroSection.Description>
            </div>

            <div className="flex flex-col gap-x-10 lg:gap-x-20 gap-y-12 text-center justify-center md:flex-row">
              <Section
                title="Book a Demo"
                icon={CalendarIcon}
                action={
                  <DiscoverEnerfloButtonWithOverlay
                    buttonText="Book a Demo"
                    buttonClassName="text-base w-full xs:w-full"
                  />
                }
              >
                <strong className="font-bold text-white">Book a Discovery Call</strong> with an Enerflo Platform
                Consultant to find out if Enerflo is a good fit for your business, get pricing info and more.
              </Section>

              <Section
                title="Request a Callback"
                icon={CallPhoneIcon}
                action={<RequestCallbackButton buttonClassName="xs:w-full" />}
              >
                <strong className="font-bold text-white">Please provide us with a few details</strong> so we know how
                best to reach you for a callback from an Enerflo Platform Consultant.
              </Section>

              <Section
                title="Get Help & Support"
                icon={SupportIcon}
                action={
                  <Button
                    href="/support"
                    className="w-full md:w-full text-center uppercase font-bold"
                    onClick={() => sendAnalyticEvent("Utility Button", "Buttons", "Get Help & Support")}
                  >
                    Get Help & Support
                  </Button>
                }
              >
                <strong className="font-bold text-white">Contact Enerflo’s Support Team</strong> for help logging into
                Enerflo, help using Enerflo, or with any other questions or problems.
              </Section>
            </div>
          </HeroSection.Inner>
        </HeroSectionContainer>
      </div>
    </div>
  );
};

export default DemoFormSection;
